// Public page: /filing/:token — the CLIENT reviews a prepared agency filing
// (FTC identity-theft report or CFPB complaint) and signs their attestation.
//
// This is the CROA gate for automated filling: staff / Attorney Brian cannot
// pull the portal fill data until the client has signed here. Token-keyed
// public page (same trust model as /intake/:token); uses raw fetch so the
// authed API helper never redirects it to /login.
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { PortalShell } from "../components/PortalShell";

interface FilingSummary {
  kind: string;
  agency: string | null;
  narrative: string | null;
  bureaus: string[] | null;
  accounts: Array<{ company: string; description: string }> | null;
  // CFPB complaint against a collection agency
  target?: "collector";
  company?: { name_as_reported: string } | null;
  wizard?: { issue: string; sub_issue: string; tried_to_fix: boolean; requested_information: boolean; company_provided_information: boolean | null } | null;
  resolution?: string | null;
  account_number_as_reported?: string | null;
}
interface ReviewState {
  ok: boolean;
  client_name: string;
  legal_name: string;
  attested: boolean;
  attested_at: string | null;
  /** Version of the filing shown on this page; the signature is tied to it. */
  draft_revision?: string;
  statement: string;
  filing: FilingSummary;
}

const KIND_LABEL: Record<string, string> = {
  ftc_identity_theft: "FTC Identity Theft Report (IdentityTheft.gov)",
  cfpb_complaint: "CFPB Consumer Complaint",
  cfpb_collector_complaint: "CFPB Complaint against a debt collector",
};

export default function FilingReviewPage() {
  const { token = "" } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ReviewState | null>(null);

  const [typedName, setTypedName] = useState("");
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [changed, setChanged] = useState(false);

  useEffect(() => {
    if (!token) { setError("This link is missing its code. Ask your credit repair company for a new one."); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/filing-review/${encodeURIComponent(token)}`);
        const body = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setError(body?.message || "This filing link is not valid or has expired.");
        } else {
          setData(body as ReviewState);
        }
      } catch {
        if (!cancelled) setError("Could not load the filing. Check your connection and reload.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const onSubmit = useCallback(async () => {
    setSubmitErr(null);
    if (typedName.trim().length < 3) { setSubmitErr("Type your full legal name as your signature."); return; }
    if (!agree) { setSubmitErr("Please check the box to agree to the statement."); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`/api/filing-review/${encodeURIComponent(token)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // The signature covers exactly the version shown on this page.
        body: JSON.stringify({ attestation_name: typedName.trim(), agree: true, draft_revision: data?.draft_revision }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 409 && body?.error === "filing_changed") {
          setSubmitErr("This filing was updated after you opened this page, so your signature wasn't recorded. Reload the page, read the updated version, then sign.");
          setChanged(true);
        } else
        setSubmitErr(body?.message || "Could not record your signature. Try again.");
      } else {
        setDone(true);
      }
    } catch {
      setSubmitErr("Could not record your signature. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }, [token, typedName, agree, data?.draft_revision]);

  if (loading) {
    return (
      <PortalShell>
        <div className="portal-card" role="status" aria-live="polite">
          <p className="portal-sub on-card">Loading your filing…</p>
        </div>
      </PortalShell>
    );
  }
  if (error) {
    return (
      <PortalShell>
        <div className="portal-card" role="alert">
          <h1 className="portal-headline on-card" style={{ fontSize: 24 }}>This link can't be used.</h1>
          <p className="portal-sub on-card">{error}</p>
        </div>
      </PortalShell>
    );
  }
  if (!data) return null;

  const f = data.filing;
  const label = KIND_LABEL[f.kind] ?? "Agency filing";
  const alreadyDone = data.attested || done;

  return (
    <PortalShell>
      <section className="portal-section">
        <p className="portal-eyebrow">Review &amp; sign</p>
        <h1 className="portal-headline">{data.client_name}, review what will be filed in your name.</h1>
        <p className="portal-sub">
          Your credit repair company prepared a <strong>{label}</strong> for you. Read it carefully —
          it will be filed with a government agency, and it must be true.
        </p>

        <div className="portal-card" style={{ marginTop: 16, display: "grid", gap: 12 }}>
          {f.bureaus && f.bureaus.length > 0 && (
            <div>
              <strong>Companies named:</strong>{" "}
              {f.bureaus.map((b) => b.charAt(0).toUpperCase() + b.slice(1)).join(", ")}
            </div>
          )}
          {f.target === "collector" && (
            <>
              <div><strong>Company this complaint is against:</strong> {f.company?.name_as_reported || "—"}</div>
              {f.account_number_as_reported && <div><strong>Account:</strong> {f.account_number_as_reported}</div>}
              {f.wizard && (
                <div>
                  <strong>What the complaint says:</strong>
                  <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
                    <li>Problem: {f.wizard.issue} — {f.wizard.sub_issue}</li>
                    <li>You already tried to fix this with the company: {f.wizard.tried_to_fix ? "Yes" : "No"}</li>
                    <li>You asked the company for information: {f.wizard.requested_information ? "Yes" : "No"}</li>
                    {f.wizard.company_provided_information != null && <li>The company provided it: {f.wizard.company_provided_information ? "Yes" : "No"}</li>}
                  </ul>
                  <p className="portal-sub on-card" style={{ margin: "6px 0 0" }}>If any of these is not true for you, do not sign — tell your credit repair company.</p>
                </div>
              )}
              {f.resolution && (
                <div>
                  <strong>What you are asking for:</strong>
                  <p style={{ whiteSpace: "pre-wrap", margin: "6px 0 0" }}>{f.resolution}</p>
                </div>
              )}
            </>
          )}
          {f.narrative && (
            <div>
              <strong>Your statement:</strong>
              <p style={{ whiteSpace: "pre-wrap", margin: "6px 0 0" }}>{f.narrative}</p>
            </div>
          )}
          {f.accounts && f.accounts.length > 0 && (
            <div>
              <strong>Accounts identified:</strong>
              <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
                {f.accounts.map((a, i) => (
                  <li key={i}>{a.company}{a.description ? ` — ${a.description}` : ""}</li>
                ))}
              </ul>
            </div>
          )}
          {!f.narrative && (!f.accounts || f.accounts.length === 0) && (
            <p className="portal-sub on-card" style={{ margin: 0 }}>
              The filing details are being prepared. If nothing appears here, contact your credit repair company before signing.
            </p>
          )}
        </div>

        {alreadyDone ? (
          <div className="portal-card" style={{ marginTop: 16 }} role="status">
            <h2 style={{ marginTop: 0 }}>Signed ✓</h2>
            <p className="portal-sub on-card" style={{ margin: 0 }}>
              {done
                ? "Thank you. Your signature is recorded — your credit repair company can now proceed with the filing. If a text-message code arrives on your phone during the filing, share it with them to confirm."
                : `This filing was already signed${data.attested_at ? ` on ${new Date(data.attested_at).toLocaleDateString()}` : ""}. Nothing more is needed.`}
            </p>
          </div>
        ) : (
          <div className="portal-card" style={{ marginTop: 16, display: "grid", gap: 12 }}>
            <h2 style={{ margin: 0 }}>Your attestation</h2>
            <p className="portal-sub on-card" style={{ margin: 0, whiteSpace: "pre-wrap" }}>{data.statement}</p>
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ marginTop: 3 }} />
              <span>I have read the information above and I agree to this statement.</span>
            </label>
            <label style={{ display: "grid", gap: 6 }}>
              <span>Type your full legal name as your signature</span>
              <input
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                placeholder={data.legal_name}
                autoComplete="name"
              />
            </label>
            {submitErr && <div className="banner-warning" role="alert">{submitErr}</div>}
            {changed && <button type="button" className="primary-button" onClick={() => window.location.reload()}>Reload the updated filing</button>}
            <button className="btn-primary" onClick={onSubmit} disabled={submitting} style={{ justifySelf: "start" }}>
              {submitting ? "Recording…" : "Sign & continue"}
            </button>
          </div>
        )}
      </section>
    </PortalShell>
  );
}
